// @flow
import React from 'react';
import type { ChildrenArray } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import { loadImage } from '../../loadImage.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import type { MathString } from '../../types.js';
import { mathStringChecker } from '../../types.js';
import { HandToolsIcon } from './icons.js';
import { InternalCallOutList } from './InternalCallOutList.js';
import { InternalAnchor } from '../common-components/internal-components.js';
import { nonMathspaceUrlChecker } from '../../ExternalLink.js';
import { Text } from '../../Text.js';
import Image from '../../Image.js';
import DraftImage from '../../DraftImage.js';
import LatexInternal from '../../LatexInternal.js';
import { CartesianPlane } from '../../CartesianPlane.js';
import { INVTechnologyToolScreenshot } from '../common-components/INVTechnologyToolScreenshot.js';
import { ContentContext } from '../../JsxContent.js';
import { InternalGraphic, getGraphicSelections } from './InternalGraphic.js';
import {
  illustrationColor,
  productColor,
  productColorSunflower,
} from '../../designSystem.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import { pathChecker } from '../../Image.js';
import { isAbortError } from '../../utilities.js';
import { useValidationApiOrigin } from '../../validation/ValidationApiOriginContext.js';

export function INVProcedure(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVProcedurePreamble>
    | React$Element<typeof INVProcedureRequirements>
    | React$Element<typeof INVSteps>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVProcedure);

  if (failedValidation) return null;

  const { children } = props;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: 24,
      }}
    >
      {children}
    </div>
  );
}
INVProcedure.spec = {
  children: childrenArrayMatcher(
    { min: 0, max: 1, elementType: t.elementType(INVProcedurePreamble) },
    { min: 0, max: 1, elementType: t.elementType(INVProcedureRequirements) },
    { min: 1, max: 1, elementType: t.elementType(INVSteps) },
  ),
};

export function INVProcedurePreamble(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVProcedurePreamble);

  if (failedValidation) return null;

  const { children } = props;

  return <div>{children}</div>;
}
INVProcedurePreamble.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVProcedureRequirements(props: {|
  children: ChildrenArray<React$Element<typeof INVRequirementItem>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVProcedureRequirements,
  );
  if (failedValidation) return null;

  return (
    <InternalCallOutList title="What you'll need" icon={<HandToolsIcon />}>
      {props.children}
    </InternalCallOutList>
  );
}
INVProcedureRequirements.spec = {
  children: t.childrenArray(t.elementType(INVRequirementItem)),
};

export function INVRequirementItem(props: {|
  children: string,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVRequirementItem);
  if (failedValidation) return null;

  return props.children;
}
INVRequirementItem.spec = {
  children: t.string,
};

export function INVSteps(props: {|
  shuffleGraphicsSeed: string,
  children: ChildrenArray<
    React$Element<typeof INVStep> | React$Element<typeof INVResourceLink>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVSteps);
  if (failedValidation) return null;

  const { children, shuffleGraphicsSeed } = props;
  const graphicSelections = getGraphicSelections(shuffleGraphicsSeed);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 40,
      }}
    >
      {React.Children.map(children, (child, idx) =>
        // $FlowIssue bad cloneElement signature
        React.cloneElement(child, {
          _graphicSelection: graphicSelections[idx % graphicSelections.length],
        }),
      )}
    </div>
  );
}
INVSteps.spec = {
  shuffleGraphicsSeed: t.uuid,
  children: t.childrenArray(
    t.unionOfTypes(t.elementType(INVStep), t.elementType(INVResourceLink)),
  ),
};

type StepsItemChildrenArray = ChildrenArray<
  | React$Element<typeof INVStepText>
  | React$Element<typeof INVStepList>
  | React$Element<typeof INVStepQuestions>
  | React$Element<typeof INVStepFigure>,
>;

type StepItemProps = {|
  children: StepsItemChildrenArray,
  _graphicSelection: number,
  ...CompilerInjectedProps,
|};

const stepsItemSpec = {
  children: t.unionOfTypes(
    t.elementType(INVStepText),
    t.elementType(INVStepList),
    t.exactOrderChildrenArray(
      t.elementType(INVStepText),
      t.elementType(INVStepFigure),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVStepList),
      t.elementType(INVStepFigure),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVStepText),
      t.elementType(INVStepQuestions),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVStepList),
      t.elementType(INVStepQuestions),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVStepText),
      t.elementType(INVStepQuestions),
      t.elementType(INVStepFigure),
    ),
    t.exactOrderChildrenArray(
      t.elementType(INVStepList),
      t.elementType(INVStepQuestions),
      t.elementType(INVStepFigure),
    ),
  ),
};

export function INVStep(props: StepItemProps) {
  const { failedValidation } = usePropsValidation(props, INVStep);

  const { enablePhoneLayout } = React.useContext(ContentContext);

  if (failedValidation) return null;

  const { _graphicSelection } = props;
  const childrenList = React.Children.toArray(props.children);
  const maybeFigure = childrenList.find(
    (child) => child.type?.name === INVStepFigure.name,
  );
  const textChildren = (childrenList.filter(
    (child) => child.type?.name !== INVStepFigure.name,
  ): Array<React$Element<any>>);
  const [textChild, maybeQuestions] = textChildren;
  const largeFigure = (maybeFigure !== undefined &&
    maybeFigure.props.children &&
    React.Children.toArray(maybeFigure.props.children).some((child) => {
      if (
        [INVTechnologyToolScreenshot.name, INVStepTable.name].includes(
          child.type.name,
        )
      )
        return true;
      if (
        [Image.name, DraftImage.name].includes(child.type.name) &&
        child.props.width > 260
      )
        return true;
      return false;
    }): boolean);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        flexDirection:
          enablePhoneLayout || largeFigure ? 'column-reverse' : 'row',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          gap: 8,
          flexDirection: 'column',
          ...(enablePhoneLayout || largeFigure
            ? { marginTop: -16 }
            : {
                maxWidth: 'calc(100% - 368px)',
                marginRight: -16,
              }),
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            borderRadius: 12,
            backgroundColor: illustrationColor.porcelain,
            ...(enablePhoneLayout || largeFigure
              ? { padding: '32px 16px 16px 16px' }
              : {
                  padding: '16px 32px 16px 16px',
                }),
          }}
        >
          {textChild}
        </div>
        {maybeQuestions && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              borderRadius: 12,
              backgroundColor: productColor.aliceBlue,
              ...(enablePhoneLayout || largeFigure
                ? { padding: 16 }
                : {
                    padding: '16px 32px 16px 16px',
                  }),
            }}
          >
            {maybeQuestions}
          </div>
        )}
      </div>
      {maybeFigure === undefined ? (
        <StepGraphic graphicSelection={_graphicSelection} />
      ) : (
        // $FlowIssue - bad cloneElement signature
        React.cloneElement(maybeFigure, { _largeFigure: largeFigure })
      )}
    </div>
  );
}
INVStep.spec = stepsItemSpec;

export function INVStepText(props: {|
  children: ChildrenArray<React$Element<typeof Text>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVStepText);
  if (failedValidation) return null;

  return <div>{props.children}</div>;
}
INVStepText.spec = {
  children: t.childrenArray(t.elementType(Text)),
};

export function INVStepList(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVStepListContext>
    | React$Element<typeof INVStepListItem>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVStepList);
  if (failedValidation) return null;

  const childrenList = React.Children.toArray(props.children);
  const [context, ...items] = childrenList;
  const hasContext = context.type?.name === INVStepListContext.name;

  return (
    <div>
      {hasContext && context}
      <ul style={{ margin: 0 }}>
        {(hasContext ? items : childrenList).map((child, idx) => (
          <li key={idx}>{child}</li>
        ))}
      </ul>
    </div>
  );
}
INVStepList.spec = {
  children: childrenArrayMatcher(
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVStepListContext),
    },
    {
      // We want to be more certain that lists are being used to list things,
      // hence there must be multiple items.
      min: 2,
      max: Infinity,
      elementType: t.elementType(INVStepListItem),
    },
  ),
};

export function INVStepListContext(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVStepListContext);
  if (failedValidation) return null;

  return props.children;
}
INVStepListContext.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVStepListItem(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVStepListItem);
  if (failedValidation) return null;

  return props.children;
}
INVStepListItem.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVStepQuestions(props: {|
  children: ChildrenArray<React$Element<typeof INVStepQuestion>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVStepQuestions);
  if (failedValidation) return null;

  const childrenList = React.Children.toArray(props.children);

  const isSingletonQuestion = childrenList.length === 1;

  return (
    <div>
      {isSingletonQuestion ? (
        childrenList
      ) : (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {childrenList.map((child, idx) => (
            <li key={idx}>{child}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
INVStepQuestions.spec = {
  children: t.childrenArray(t.elementType(INVStepQuestion)),
};

export function INVStepQuestion(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVStepQuestion);
  if (failedValidation) return null;

  return props.children;
}
INVStepQuestion.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVStepFigure(props: {|
  children:
    | React$Element<typeof Image>
    | React$Element<typeof DraftImage>
    | React$Element<typeof CartesianPlane>
    | React$Element<typeof INVInstructionalPhoto>
    | React$Element<typeof INVSpreadsheetScreenshot>
    | React$Element<typeof INVTechnologyToolScreenshot>
    | React$Element<typeof INVStepTable>,
  //currently large figures are tech screenshots and tables
  _largeFigure?: boolean,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVStepFigure);

  const { enablePhoneLayout } = React.useContext(ContentContext);

  if (failedValidation) return null;

  const { _largeFigure, children } = props;

  const extraStyling = ![
    INVInstructionalPhoto.name,
    INVTechnologyToolScreenshot.name,
    INVStepTable.name,
    INVSpreadsheetScreenshot.name,
  ].includes(children.type.name)
    ? {
        padding: 10,
        border: `solid 2px ${productColor.ironDark}`,
        backgroundColor: productColor.white,
      }
    : {};
  return (
    <div
      style={{
        borderRadius: 12,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1,
        boxShadow: '0px 8.75px 49px rgba(62, 62, 76, 0.15)',
        flexShrink: 0,
        maxWidth: _largeFigure ? 596 : 384,
        overflow: 'hidden',
        margin: enablePhoneLayout || _largeFigure ? 0 : '16px 0 0 0',
        ...extraStyling,
      }}
    >
      {/* $FlowIssue bad cloneElement signature */}
      {React.cloneElement(children, { _removeFigureMargin: true })}
    </div>
  );
}
INVStepFigure.spec = {
  children: t.unionOfTypes(
    t.elementType(Image),
    t.elementType(DraftImage),
    t.elementType(CartesianPlane),
    t.elementType(INVInstructionalPhoto),
    t.elementType(INVSpreadsheetScreenshot),
    t.elementType(INVTechnologyToolScreenshot),
    t.elementType(INVStepTable),
  ),
};

function StepGraphic(props: {| graphicSelection: number |}) {
  const { enablePhoneLayout } = React.useContext(ContentContext);
  return (
    <div
      style={{
        width: 284,
        height: 284,
        boxShadow: '0px 8.75px 49px rgba(62, 62, 76, 0.15)',
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 12,
        zIndex: 1,
        backgroundColor: productColor.papayaWhip,
        margin: enablePhoneLayout ? '0 16px 0 0' : '16px 0 0 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <InternalGraphic selection={props.graphicSelection} />
        <div
          style={{
            position: 'absolute',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}
          >
            <svg width="96" height="96" viewBox="0 0 96 96" fill="none">
              <rect
                width="96"
                height="96"
                rx="48"
                fill={illustrationColor.cinnamon}
              />
              <path
                d="M42.3392 37.5435C43.0352 36.0566 43.3471 34.3537 43.1552 32.531C42.6992 28.3818 39.4596 24.8562 35.332 24.1607C28.6847 23.0095 22.9974 28.6936 24.1492 35.3131C24.8692 39.4383 28.3728 42.676 32.5243 43.1317C34.3481 43.3236 36.0279 43.0118 37.5398 42.3163L43.2031 48.0004L37.5398 53.6605C36.0519 52.965 34.3481 52.6532 32.5243 52.8451C28.3728 53.3008 24.8452 56.5386 24.1492 60.6638C22.9974 67.3072 28.6847 72.9674 35.308 71.8161C39.4356 71.0966 42.6752 67.595 43.1312 63.4459C43.3231 61.6231 43.0112 59.9443 42.3152 58.4333L48.0026 52.7971L66.2166 71.0007C68.3524 73.1352 72 71.6243 72 68.6023V68.5783C72 67.691 71.64 66.8275 71.0161 66.18L42.3392 37.5435ZM33.6042 38.407C30.9645 38.407 28.8047 36.2724 28.8047 33.6102C28.8047 30.9481 30.9645 28.8135 33.6042 28.8135C36.2439 28.8135 38.4037 30.9481 38.4037 33.6102C38.4037 36.2724 36.2439 38.407 33.6042 38.407ZM33.6042 67.1873C30.9645 67.1873 28.8047 65.0528 28.8047 62.3906C28.8047 59.7284 30.9645 57.5939 33.6042 57.5939C36.2439 57.5939 38.4037 59.7284 38.4037 62.3906C38.4037 65.0528 36.2439 67.1873 33.6042 67.1873ZM66.2166 25.0001L50.4024 40.8053L55.2018 45.602L71.0161 29.7968C71.64 29.1733 72 28.3099 72 27.3985V27.3745C72 24.3765 68.3524 22.8656 66.2166 25.0001Z"
                fill={productColor.white}
              />
            </svg>
            <svg width="96" height="96" viewBox="0 0 96 96" fill="none">
              <rect
                width="96"
                height="96"
                rx="48"
                fill={illustrationColor.cinnamon}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M48.2648 18H71.3368C72.2258 18 72.9466 18.7208 72.9466 19.6098V71.3383C72.9466 72.2273 72.2258 72.9481 71.3368 72.9481H67.4651C66.8324 72.9481 66.2584 72.5775 65.998 72.0009L63.8276 67.195C63.4908 66.4493 62.7418 65.9756 61.9269 65.9909C59.0687 66.0409 55.5582 66.0938 53.767 66.0582C51.8399 66.02 50.7167 65.2019 50.1138 64.5223C49.2785 63.5807 48.8952 62.2943 49.0346 60.9002C49.1155 60.0923 48.8972 59.817 48.4623 59.2689L48.4615 59.2679C48.1328 58.8534 47.724 58.3379 47.5268 57.6038C47.2272 56.4885 47.6649 55.2728 48.642 54.5066C48.6858 54.4724 48.7311 54.4403 48.7782 54.4108L52.0128 52.3761L47.1784 50.542C47.1372 50.5264 47.0967 50.509 47.0568 50.49C45.9022 49.9397 45.2629 48.6776 45.5023 47.4211L46.1277 44.1383C46.1913 43.8037 46.1177 43.4665 45.9202 43.1888C45.7227 42.9112 45.4284 42.731 45.0912 42.6813L41.9087 42.2135C41.0279 42.0839 40.273 41.5271 39.8889 40.7242C39.5049 39.9215 39.5454 38.9843 39.9971 38.2175L46.0629 27.9214C46.9856 26.3553 47.3497 24.5022 47.0881 22.7034L46.6718 19.8416C46.6046 19.3791 46.7415 18.9102 47.0474 18.5567C47.3532 18.2032 47.7975 18 48.2648 18ZM26.4201 41.209C27.0488 40.5804 27.0488 39.5611 26.4201 38.9324C25.7915 38.3038 24.7722 38.3038 24.1435 38.9324C15.9522 47.1238 15.9522 60.4522 24.1435 68.6436C24.4578 68.9579 24.8698 69.1151 25.2817 69.1151C25.6936 69.1151 26.1057 68.9579 26.42 68.6436C27.0487 68.015 27.0487 66.9957 26.42 66.367C19.4841 59.431 19.4841 48.1451 26.4201 41.209ZM30.1994 43.7664C30.8281 43.1378 31.8474 43.1378 32.476 43.7664C33.1047 44.395 33.1047 45.4144 32.4759 46.0429C28.2034 50.3154 28.2034 57.2672 32.4759 61.5398C33.1046 62.1684 33.1046 63.1878 32.4759 63.8164C32.1617 64.1307 31.7496 64.2879 31.3377 64.2879C30.9258 64.2879 30.5138 64.1307 30.1994 63.8164C24.6716 58.2885 24.6716 49.2942 30.1994 43.7664ZM36.2377 48.7292C36.8664 48.1007 37.8857 48.1007 38.5143 48.7292C39.143 49.3578 39.143 50.3771 38.5142 51.0058C36.9798 52.5401 36.9798 55.0368 38.5142 56.5711C39.1429 57.1998 39.1429 58.2191 38.5142 58.8477C38.2 59.1621 37.7878 59.3193 37.376 59.3193C36.9641 59.3193 36.5521 59.1621 36.2377 58.8477C33.448 56.058 33.448 51.5189 36.2377 48.7292Z"
                fill={productColor.white}
              />
            </svg>
            <svg width="97" height="96" viewBox="0 0 97 96" fill="none">
              <rect
                x="0.126221"
                width="96"
                height="96"
                rx="48"
                fill={illustrationColor.cinnamon}
              />
              <path
                d="M60.6866 59.4997L59.626 60.5604C58.4544 61.732 58.4544 63.6314 59.626 64.803L71.3542 76.5313C72.5258 77.7028 74.4253 77.7028 75.5968 76.5313L76.6575 75.4706C77.8291 74.299 77.8291 72.3995 76.6575 71.228L64.9293 59.4997C63.7577 58.3281 61.8582 58.3281 60.6866 59.4997Z"
                fill={productColor.white}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M65.3658 39.37C65.3658 52.829 54.4551 63.7397 40.996 63.7397C27.5369 63.7397 16.6262 52.829 16.6262 39.37C16.6262 25.9109 27.5369 15.0002 40.996 15.0002C54.4551 15.0002 65.3658 25.9109 65.3658 39.37ZM35.4899 24.4614C36.3383 25.0766 36.3383 26.0742 35.4899 26.6894C26.1291 33.4776 26.1291 44.5224 35.4896 51.3105C36.3383 51.9258 36.3383 52.9233 35.4896 53.5386C35.0657 53.8461 34.5095 54 33.9536 54C33.3977 54 32.8415 53.8461 32.4173 53.5386C21.3625 45.522 21.3625 32.478 32.4173 24.4614C33.266 23.8462 34.6415 23.8462 35.4899 24.4614Z"
                fill={productColor.white}
              />
            </svg>
            <svg width="96" height="96" viewBox="0 0 96 96" fill="none">
              <rect
                width="96"
                height="96"
                rx="48"
                fill={illustrationColor.cinnamon}
              />
              <path
                d="M67.98 61.98L65.85 64.11C65.25 64.71 64.32 64.71 63.72 64.11C63.12 63.51 63.12 62.58 63.72 61.98L65.85 59.85L60.03 54.03L57.9 56.16C57.3 56.76 56.37 56.76 55.77 56.16C55.17 55.56 55.17 54.63 55.77 54.03L57.9 51.9L52.08 46.08L49.95 48.21C49.35 48.81 48.42 48.81 47.82 48.21C47.22 47.61 47.22 46.68 47.82 46.08L49.95 43.95L44.1 38.1L41.97 40.23C41.37 40.83 40.44 40.83 39.84 40.23C39.24 39.63 39.24 38.7 39.84 38.1L41.97 35.97L36.15 30.15L34.02 32.28C33.42 32.88 32.49 32.88 31.89 32.28C31.29 31.68 31.29 30.75 31.89 30.15L34.02 28.02L29.55 23.55C28.62 22.62 27 23.28 27 24.63V63C27 66.3 29.7 69 33 69H71.37C72.72 69 73.38 67.38 72.42 66.45L67.98 61.98ZM36 57V42.72L53.28 60H39C37.35 60 36 58.65 36 57Z"
                fill={productColor.white}
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export function INVInstructionalPhoto(props: {|
  width: number,
  height: number,
  path: string,
  accessibilityDescription: string,
  ...CompilerInjectedProps,
|}) {
  const S3_DIRECTORY =
    'https://jsx-images.mathspace.co/investigations/instructional-photos';

  const { enablePhoneLayout } = React.useContext(ContentContext);
  const { width, height, path, accessibilityDescription } = props;

  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVInstructionalPhoto,
  );

  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;

    if (width !== 384) {
      addErrors([
        {
          message: `The only valid \`width\` of an INVInstructionalPhoto is 384px.`,
          ...locations.width,
        },
      ]);
      return;
    }
    let staleEffect = false;
    (async () => {
      try {
        const imageNode = await loadImage(`${S3_DIRECTORY}${path}`);
        if (staleEffect) return;
        const { naturalWidth, naturalHeight } = imageNode;
        if (naturalWidth < 2 * width) {
          addErrors([
            {
              message: `The natural width of the image at ${path} is too small. It should be at least 2x the width of an INVInstructionalPhoto (384px).`,
              ...locations.INVInstructionalPhoto,
            },
          ]);
          return;
        }
        if (naturalWidth / naturalHeight !== width / height) {
          addErrors([
            {
              message: [
                `The natural aspect ratio of the image at ${path} is distorted by the given \'width\' and \`height\`.`,
                `For a width of 384px, the height should be ${
                  (naturalHeight * width) / naturalWidth
                }px`,
              ].join('\n'),
              ...locations.INVInstructionalPhoto,
            },
          ]);
          return;
        }
      } catch {
        if (staleEffect) return;
        addErrors([
          {
            message: `The image at ${path} could not be loaded.`,
            ...locations.path,
          },
        ]);
      }
    })();
    return () => {
      staleEffect = true;
    };
  });

  if (failedValidation) return null;

  return (
    <img
      style={{ maxWidth: width }}
      width={enablePhoneLayout ? '100%' : width}
      height={enablePhoneLayout ? 'auto' : height}
      src={`${S3_DIRECTORY}${path}`}
      alt={accessibilityDescription}
    />
  );
}
INVInstructionalPhoto.spec = {
  width: t.number,
  height: t.number,
  path: pathChecker,
  accessibilityDescription: t.string,
};

export function INVSpreadsheetScreenshot(props: {|
  width: number,
  height: number,
  path: string,
  accessibilityDescription: string,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVSpreadsheetScreenshot,
  );

  const S3_DIRECTORY =
    'https://jsx-images.mathspace.co/investigations/spreadsheet-screenshots';
  const { enablePhoneLayout } = React.useContext(ContentContext);
  const { width, height, path, accessibilityDescription } = props;

  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;

    if (width !== 384) {
      addErrors([
        {
          message: `The only valid \`width\` of an INVSpreadsheetScreenshot is 384px.`,
          ...locations.width,
        },
      ]);
      return;
    }
    let staleEffect = false;
    (async () => {
      try {
        const imageNode = await loadImage(`${S3_DIRECTORY}${path}`);
        if (staleEffect) return;
        const { naturalWidth, naturalHeight } = imageNode;
        if (naturalWidth < 2 * width) {
          addErrors([
            {
              message: `The natural width of the image at ${path} is too small. It should be at least 2x the width of an INVInstructionalPhoto (384px).`,
              ...locations.INVSpreadsheetScreenshot,
            },
          ]);
          return;
        }
        if (naturalWidth / naturalHeight !== width / height) {
          addErrors([
            {
              message: [
                `The natural aspect ratio of the image at ${path} is distorted by the given \'width\' and \`height\`.`,
                `For a width of 384px, the height should be ${
                  (naturalHeight * width) / naturalWidth
                }px`,
              ].join('\n'),
              ...locations.INVSpreadsheetScreenshot,
            },
          ]);
          return;
        }
      } catch {
        if (staleEffect) return;
        addErrors([
          {
            message: `The image at ${path} could not be loaded.`,
            ...locations.path,
          },
        ]);
      }
    })();
    return () => {
      staleEffect = true;
    };
  });

  if (failedValidation) return null;

  return (
    <img
      style={{ maxWidth: width }}
      width={enablePhoneLayout ? '100%' : width}
      height={enablePhoneLayout ? 'auto' : height}
      src={`${S3_DIRECTORY}${path}`}
      alt={accessibilityDescription}
    />
  );
}
INVSpreadsheetScreenshot.spec = {
  width: t.number,
  height: t.number,
  path: pathChecker,
  accessibilityDescription: t.string,
};

export function INVStepTable(props: {
  rowTitles?: Array<string>,
  rowTitlesAs?: 'text' | 'latex',
  columnTitles?: Array<string>,
  columnTitlesAs?: 'text' | 'latex',
  rowData: Array<Array<string>>,
  rowDataAs: 'text' | 'latex',
  ...CompilerInjectedProps,
}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVStepTable,
  );

  const {
    rowTitles = null,
    rowTitlesAs = null,
    columnTitles = null,
    columnTitlesAs = null,
    rowData,
    rowDataAs,
    _performValidation,
  } = props;

  // Check each prop is minimally populated
  React.useEffect(() => {
    if (failedValidation || !_performValidation) return;

    if (rowData.length === 0) {
      addErrors([
        {
          message: '`rowData` must have at least one row.',
          ...locations.rowData,
        },
      ]);
      return;
    }
    if (rowData.some((row) => row.length === 0)) {
      addErrors([
        {
          message: 'Each row in `rowData` must have at least one column.',
          ...locations.rowData,
        },
      ]);
      return;
    }
    if (rowTitles !== null && rowTitles.length === 0) {
      addErrors([
        {
          message: '`rowTitles` must have at least one title.',
          ...locations.rowTitles,
        },
      ]);
      return;
    }
    if (columnTitles !== null && columnTitles.length === 0) {
      addErrors([
        {
          message: '`columnTitles` must have at least one title.',
          ...locations.columnData,
        },
      ]);
      return;
    }
  });

  // Check row titles are valid
  React.useEffect(() => {
    if (failedValidation || !_performValidation) return;
    if (rowTitles === null && rowTitlesAs === null) return;
    if (rowTitles === null || rowTitlesAs === null) {
      addErrors([
        {
          message:
            '`rowTitles` and `rowTitlesAs` must be provided together, or not at all.',
          ...locations.INVStepTable,
        },
      ]);
      return;
    }
    if (rowTitles.length !== rowData.length) {
      addErrors([
        {
          message: `The number of titles in rowTitles must match the number of rows in rowData`,
          ...locations.rowTitles,
        },
      ]);
      return;
    }
  });

  // Check column titles are valid
  React.useEffect(() => {
    if (failedValidation || !_performValidation) return;
    const rowLengths =
      rowData.length === 0 ? null : rowData.map((row) => row.length);
    if (
      rowLengths !== null &&
      Math.min(...rowLengths) !== Math.max(...rowLengths)
    ) {
      addErrors([
        {
          message: `All rows must have the same number of items.`,
          ...props._sourceLocations?.rowData,
        },
      ]);
      return;
    }

    if (columnTitles === null && columnTitlesAs === null) return;
    if (columnTitles === null || columnTitlesAs === null) {
      addErrors([
        {
          message:
            '`columnTitles` and `columnTitlesAs` must be provided together, or not at all.',
          ...locations.INVStepTable,
        },
      ]);
      return;
    }
    // Length parity across the rows has already been checked
    // so only checking the first row is sufficient.
    if (columnTitles.length !== rowData[0].length) {
      addErrors([
        {
          message: `The number of titles in columnTitles must match the number of columns in rowData`,
          ...locations.columnTitles,
        },
      ]);
      return;
    }
  });

  React.useEffect(() => {
    // TODO these error messages look pretty lame (especially when >1 latex errors for a given list)
    function validateLatexFor(propName, propValue) {
      const errors = propValue
        .map((latexString) => {
          // We create an object to adhere to the "props" interface of checkers
          return t.latex({ latexString }, 'latexString');
        })
        .filter(Boolean);

      if (errors.length > 0) {
        addErrors([
          {
            message: `Invalid usage(s) of latex:\n - ${errors
              .map((e) => e.reason)
              .join('\n - ')}`,
            ...props._sourceLocations?.[propName],
          },
        ]);
      }
    }
    if (rowTitles && rowTitlesAs === 'latex')
      validateLatexFor('rowTitles', rowTitles);
    if (columnTitles && columnTitlesAs === 'latex')
      validateLatexFor('columnTitles', columnTitles);
    if (rowData && rowDataAs === 'latex')
      validateLatexFor('rowData', rowData.flat());
  });

  const latexOrText = (as, content) => {
    switch (as) {
      case 'latex':
        return <LatexInternal expression={content} />;
      case 'text':
        return content;
      default:
        (as: empty);
        throw new Error(`Invalid choice: must be either 'latex' or 'text'`);
    }
  };

  const BORDER_SPACING = 4;
  const cellStyle = {
    padding: '8px 16px',
  };
  const tableHeaderStyle = {
    fontWeight: 600,
    color: illustrationColor.paco,
    backgroundColor: illustrationColor.porcelain,
    ...cellStyle,
  };

  const tableDataStyle = {
    backgroundColor: productColorSunflower.seashell,
    ...cellStyle,
  };

  if (failedValidation) return null;

  return (
    // this container div makes sure that the border spacing on the exterior
    // of the table is covered and the tables extends to the edge of the figure
    <div
      style={{ margin: -BORDER_SPACING, backgroundColor: productColor.white }}
    >
      <table
        style={{
          fontSize: 16,
          borderSpacing: BORDER_SPACING,
        }}
      >
        {columnTitles && (
          <thead>
            <tr>
              {rowTitles && <th style={tableHeaderStyle} />}
              {columnTitles.map((title, idx) => (
                <th key={idx} style={tableHeaderStyle}>
                  {columnTitlesAs && latexOrText(columnTitlesAs, title)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rowData.map((row, idx) => (
            <tr key={idx}>
              {rowTitles && (
                <th style={tableHeaderStyle}>
                  {rowTitlesAs && latexOrText(rowTitlesAs, rowTitles[idx])}
                </th>
              )}
              {row.map((cell, idx) => (
                <td key={idx} style={tableDataStyle}>
                  {rowDataAs && latexOrText(rowDataAs, cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
INVStepTable.spec = {
  rowTitles: t.optional(t.arrayOf(t.string)),
  rowTitlesAs: t.optional(t.unionOfValues('latex', 'text')),
  columnTitles: t.optional(t.arrayOf(t.string)),
  columnTitlesAs: t.optional(t.unionOfValues('latex', 'text')),
  rowData: t.arrayOf(t.arrayOf(t.string)),
  rowDataAs: t.unionOfValues('latex', 'text'),
};

export function INVResourceLink(props: {|
  url: string,
  title: string,
  children: ChildrenArray<React$Element<typeof Text>>,
  _graphicSelection: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVResourceLink,
  );

  const { enablePhoneLayout } = React.useContext(ContentContext);
  const apiOrigin = useValidationApiOrigin();
  const { url, title, children, _graphicSelection, _performValidation } = props;

  // Verify that the url can be loaded, and that it returns a 200.
  // Because these URLs point to external resources, they will die
  // over time. If the URL starts returning a 404, or some redirect
  // status, its a clear signal that the owner of the resource has
  // messed with it and the resource is probably busted in some way.
  React.useEffect(() => {
    if (failedValidation || !_performValidation) return;

    const abortController = new AbortController();
    (async () => {
      const encodedUrl = encodeURIComponent(url);
      const serverlessUrl = `${apiOrigin}/api/resource-link-check?url=${encodedUrl}`;
      try {
        const res = await fetch(serverlessUrl, {
          signal: abortController.signal,
        });
        if (!res.ok) throw Error();
        const json = await res.json();
        if (json.outcome === 'failure') throw Error();
        if (json.statusCode !== 200) {
          addErrors([
            {
              message: `The url ${url} returned a ${json.statusCode} status code. It must return a 200. This likely indicates the owner of this website has modified the site in a way that may break this investigation.`,
              ...locations.url,
            },
          ]);
        }
      } catch (error) {
        if (isAbortError(error)) return;
        addErrors([
          {
            message: `When checking ${url} to see if its a valid resource, something went wrong. Since we could not determine its validity, this validation error exists to prevent a potentially broken url from being published. Try re-validating to see if this is a transient error`,
            ...locations.url,
          },
        ]);
      }
    })();

    return () => {
      abortController.abort();
    };
  });

  if (failedValidation) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        flexDirection: enablePhoneLayout ? 'column-reverse' : 'row',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          borderRadius: 12,
          backgroundColor: illustrationColor.porcelain,
          maxWidth: enablePhoneLayout ? null : 'calc(100% - 368px)',
          ...(enablePhoneLayout
            ? { marginTop: -16, padding: '32px 16px 16px 16px' }
            : {
                marginRight: -16,
                padding: '16px 32px 16px 16px',
              }),
        }}
      >
        <div>{children}</div>
        <InternalAnchor url={url} title={title} option="link" />
      </div>
      <LinkGraphic graphicSelection={_graphicSelection} />
    </div>
  );
}
INVResourceLink.spec = {
  url: nonMathspaceUrlChecker,
  title: t.string,
  children: t.childrenArray(t.elementType(Text)),
};

function LinkGraphic({ graphicSelection }) {
  const { enablePhoneLayout } = React.useContext(ContentContext);

  return (
    <div
      style={{
        width: 284,
        height: 284,
        boxShadow: '0px 8.75px 49px rgba(62, 62, 76, 0.15)',
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 12,
        zIndex: 1,
        backgroundColor: productColor.papayaWhip,
        margin: enablePhoneLayout ? '0 16px 0 0' : '16px 0 0 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <InternalGraphic selection={graphicSelection} />
        <div
          style={{
            position: 'absolute',
          }}
        >
          <svg width="96" height="97" viewBox="0 0 96 97" fill="none">
            <rect
              y="0.332031"
              width="96"
              height="96"
              rx="48"
              fill={illustrationColor.cinnamon}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M57.2435 24.332H70.7281C71.4302 24.332 71.9994 24.9012 71.9994 25.6033V39.0876C71.9994 39.7897 71.4302 40.3589 70.7281 40.3589H67.338C66.6359 40.3589 66.0667 39.7897 66.0667 39.0876V34.4596L48.651 51.8754C48.4127 52.1138 48.0892 52.2478 47.7521 52.2478C47.4148 52.2478 47.0915 52.1138 46.853 51.8754L44.4559 49.4781C43.9594 48.9814 43.9594 48.1766 44.4559 47.6801L61.8717 30.2647H57.2435C56.5414 30.2647 55.9722 29.6955 55.9722 28.9934V25.6033C55.9722 24.9012 56.5414 24.332 57.2435 24.332ZM63.8016 48.1837H67.1917C67.8937 48.1837 68.4629 48.7529 68.4628 49.4551V64.9116C68.4628 69.0033 65.1339 72.3322 61.042 72.3322H31.4201C27.3287 72.3322 24 69.0034 24 64.9116V35.2898C24 31.198 27.3286 27.8689 31.4201 27.8689H47.6679C48.37 27.8689 48.9392 28.4381 48.9392 29.1402V32.5303C48.9392 33.2324 48.37 33.8016 47.6679 33.8016H31.4201C30.5998 33.8016 29.9327 34.4692 29.9327 35.2898V64.9114C29.9327 65.7318 30.5998 66.3993 31.4201 66.3993H61.0422C61.8626 66.3993 62.5303 65.7318 62.5303 64.9114V49.455C62.5303 48.7529 63.0995 48.1837 63.8016 48.1837Z"
              fill={productColor.white}
            />
          </svg>
        </div>
      </div>
    </div>
  );
}
